import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

// --- Types ---

interface Subscription {
  id: number;
  plan: string;
  status: string;
  period_start: string;
  period_end: string;
  limits: {
    properties: number;
    agents: number;
    media_per_property: number;
  };
}

interface SubscriptionResponse {
  data: Subscription;
}

// --- Constants ---

const PLAN_LABEL: Record<string, string> = {
  basic: 'Basico',
  professional: 'Profesional',
  enterprise: 'Enterprise',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  trial: 'Prueba',
  cancelled: 'Cancelado',
  expired: 'Vencido',
  pending: 'Pendiente',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'badge-success',
  trial: 'badge-info',
  cancelled: 'badge-error',
  expired: 'badge-ghost',
  pending: 'badge-warning',
};

interface PlanCard {
  key: string;
  name: string;
  price: string;
  period: string;
  features: string[];
  highlighted: boolean;
}

const PLANS: PlanCard[] = [
  {
    key: 'basic',
    name: 'Basico',
    price: '$9.990',
    period: '/mes',
    features: [
      'Hasta 20 propiedades',
      '1 agente',
      '10 fotos por propiedad',
      'Soporte por email',
    ],
    highlighted: false,
  },
  {
    key: 'professional',
    name: 'Profesional',
    price: '$24.990',
    period: '/mes',
    features: [
      'Hasta 100 propiedades',
      '5 agentes',
      '30 fotos por propiedad',
      'Contratos digitales',
      'Soporte prioritario',
    ],
    highlighted: true,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: '$49.990',
    period: '/mes',
    features: [
      'Propiedades ilimitadas',
      'Agentes ilimitados',
      '50 fotos por propiedad',
      'Contratos digitales',
      'API personalizada',
      'Soporte dedicado',
    ],
    highlighted: false,
  },
];

type PaymentMethod = 'mercadopago' | 'transfer';

// --- Component ---

export default function BillingPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('mercadopago');
  const [transferReference, setTransferReference] = useState('');

  const subscribeModalRef = useRef<HTMLDialogElement>(null);
  const cancelModalRef = useRef<HTMLDialogElement>(null);

  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Auto-dismiss snackbar
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // Sync modals
  useEffect(() => {
    if (subscribeModalOpen) subscribeModalRef.current?.showModal();
    else subscribeModalRef.current?.close();
  }, [subscribeModalOpen]);

  useEffect(() => {
    if (cancelModalOpen) cancelModalRef.current?.showModal();
    else cancelModalRef.current?.close();
  }, [cancelModalOpen]);

  // Fetch current subscription
  const { data: subscriptionData, isLoading } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => api.get<SubscriptionResponse>('/v1/billing/subscription'),
  });

  const subscription = subscriptionData?.data;

  // Subscribe with MercadoPago
  const subscribeMutation = useMutation({
    mutationFn: (plan: string) =>
      api.post('/v1/billing/subscribe', { plan }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      setSubscribeModalOpen(false);
      setSnackbar({ open: true, message: 'Suscripcion realizada exitosamente', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al procesar la suscripcion', severity: 'error' });
    },
  });

  // Subscribe with bank transfer
  const transferMutation = useMutation({
    mutationFn: ({ plan, reference }: { plan: string; reference: string }) =>
      api.post('/v1/billing/subscribe/transfer', { plan, reference }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      setSubscribeModalOpen(false);
      setTransferReference('');
      setSnackbar({ open: true, message: 'Transferencia registrada. Pendiente de verificacion.', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al registrar la transferencia', severity: 'error' });
    },
  });

  // Cancel subscription
  const cancelMutation = useMutation({
    mutationFn: () => api.patch('/v1/billing/subscription/cancel', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      setCancelModalOpen(false);
      setSnackbar({ open: true, message: 'Suscripcion cancelada', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al cancelar la suscripcion', severity: 'error' });
    },
  });

  const handleSubscribeClick = (planKey: string): void => {
    setSelectedPlan(planKey);
    setPaymentMethod('mercadopago');
    setTransferReference('');
    setSubscribeModalOpen(true);
  };

  const handleConfirmSubscribe = (): void => {
    if (!selectedPlan) return;
    if (paymentMethod === 'mercadopago') {
      subscribeMutation.mutate(selectedPlan);
    } else {
      if (!transferReference.trim()) {
        setSnackbar({ open: true, message: 'Ingresa la referencia de la transferencia', severity: 'error' });
        return;
      }
      transferMutation.mutate({ plan: selectedPlan, reference: transferReference.trim() });
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isProcessing = subscribeMutation.isPending || transferMutation.isPending;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Facturacion</h1>
        <p className="text-sm text-base-content/60">Gestiona tu plan y metodo de pago</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        <>
          {/* Current plan card */}
          {subscription && (
            <div className="card bg-base-100 shadow-sm mb-8">
              <div className="card-body">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold">
                      Plan {PLAN_LABEL[subscription.plan] || subscription.plan}
                    </h2>
                    <span className={`badge ${STATUS_BADGE[subscription.status] || 'badge-ghost'} mt-1`}>
                      {STATUS_LABEL[subscription.status] || subscription.status}
                    </span>
                  </div>
                  {subscription.status === 'active' && (
                    <button
                      className="btn btn-outline btn-error btn-sm"
                      onClick={() => setCancelModalOpen(true)}
                    >
                      Cancelar suscripcion
                    </button>
                  )}
                </div>

                <div className="divider my-2" />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-base-content/60">Periodo</p>
                    <p className="font-medium">
                      {formatDate(subscription.period_start)} - {formatDate(subscription.period_end)}
                    </p>
                  </div>
                  {subscription.limits && (
                    <>
                      <div>
                        <p className="text-sm text-base-content/60">Limites</p>
                        <p className="font-medium">
                          {subscription.limits.properties} propiedades, {subscription.limits.agents} agentes
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-base-content/60">Fotos por propiedad</p>
                        <p className="font-medium">{subscription.limits.media_per_property}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Plans section */}
          <div className="mb-4">
            <h2 className="text-2xl font-bold">Cambiar plan</h2>
            <p className="text-sm text-base-content/60">Selecciona el plan que mejor se adapte a tu negocio</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan) => {
              const isCurrent = subscription?.plan === plan.key;
              return (
                <div
                  key={plan.key}
                  className={`card bg-base-100 shadow-sm border-2 ${
                    isCurrent
                      ? 'border-primary'
                      : plan.highlighted
                        ? 'border-secondary'
                        : 'border-transparent'
                  }`}
                >
                  <div className="card-body">
                    {isCurrent && (
                      <span className="badge badge-primary badge-sm mb-2">Plan actual</span>
                    )}
                    {plan.highlighted && !isCurrent && (
                      <span className="badge badge-secondary badge-sm mb-2">Mas popular</span>
                    )}

                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-3xl font-bold">{plan.price}</span>
                      <span className="text-base-content/60">{plan.period}</span>
                    </div>

                    <ul className="mt-4 space-y-2">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-success flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="card-actions mt-6">
                      {isCurrent ? (
                        <button className="btn btn-outline btn-primary w-full" disabled>
                          Plan actual
                        </button>
                      ) : (
                        <button
                          className={`btn w-full ${plan.highlighted ? 'btn-secondary' : 'btn-primary btn-outline'}`}
                          onClick={() => handleSubscribeClick(plan.key)}
                        >
                          Suscribirse
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Subscribe Modal */}
      <dialog ref={subscribeModalRef} className="modal" onClose={() => setSubscribeModalOpen(false)}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">
            Suscribirse al plan {selectedPlan ? PLAN_LABEL[selectedPlan] || selectedPlan : ''}
          </h3>

          <div className="mt-4 space-y-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Metodo de pago</span>
              </label>
              <div className="flex gap-3">
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    name="payment"
                    className="radio radio-primary"
                    checked={paymentMethod === 'mercadopago'}
                    onChange={() => setPaymentMethod('mercadopago')}
                  />
                  <span className="label-text">MercadoPago</span>
                </label>
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    name="payment"
                    className="radio radio-primary"
                    checked={paymentMethod === 'transfer'}
                    onChange={() => setPaymentMethod('transfer')}
                  />
                  <span className="label-text">Transferencia bancaria</span>
                </label>
              </div>
            </div>

            {paymentMethod === 'mercadopago' && (
              <div className="alert alert-info">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Se procesara el pago a traves de MercadoPago. Seras redirigido para completar la transaccion.</span>
              </div>
            )}

            {paymentMethod === 'transfer' && (
              <div>
                <div className="alert alert-info mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Realiza una transferencia a la cuenta indicada y luego ingresa la referencia. Tu suscripcion se activara una vez verificada.</span>
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Referencia de transferencia *</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="Ej: TRF-123456789"
                    value={transferReference}
                    onChange={(e) => setTransferReference(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="modal-action">
            <button className="btn" onClick={() => setSubscribeModalOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirmSubscribe}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Confirmar suscripcion'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setSubscribeModalOpen(false)}>close</button>
        </form>
      </dialog>

      {/* Cancel Subscription Modal */}
      <dialog ref={cancelModalRef} className="modal" onClose={() => setCancelModalOpen(false)}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">Cancelar suscripcion</h3>
          <p className="py-4 text-base-content/70">
            Estas seguro que deseas cancelar tu suscripcion? Perderas acceso a las funcionalidades premium al finalizar el periodo actual.
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setCancelModalOpen(false)}>
              Volver
            </button>
            <button
              className="btn btn-error"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Confirmar cancelacion'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setCancelModalOpen(false)}>close</button>
        </form>
      </dialog>

      {/* Toast */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
